import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { DpeTableData } from '../@types/DpeTableData';
import type { Owner } from '../@types/Owner';

export interface FilteredMonth {
  date: string;
  type?: 'reception' | 'etablissement';
}

export type CompletionType = 'identified' | 'noOwner' | 'voluntarilyUntreated' | 'dpeGeneralMailing' | null;

export interface DpeState {
  selectedDpe: DpeTableData | null;
  currentStep: number | null;
  nbOwner: number | null;
  owners: Owner[];
  isDpeIdentified: boolean;
  lastCompletionType: CompletionType;
  monthFiltered?: FilteredMonth;
  monthFilteredNewDpe?: FilteredMonth;
  monthFilteredIdentifiedDpe?: FilteredMonth;
  dpeData: DpeTableData[];
  dpeClosedData: DpeTableData[];
  dpeOldData: DpeTableData[];
  dpeOldClosedData: DpeTableData[];
  dpeVoluntarilyUntreatedData: DpeTableData[];
  dpeOldVoluntarilyUntreatedData: DpeTableData[];
}

const initialState: DpeState = {
  selectedDpe: null,
  currentStep: null,
  nbOwner: null,
  owners: [],
  isDpeIdentified: false,
  lastCompletionType: null,
  monthFiltered: null,
  monthFilteredNewDpe: null,
  monthFilteredIdentifiedDpe: null,
  dpeData: [],
  dpeClosedData: [],
  dpeOldData: [],
  dpeOldClosedData: [],
  dpeVoluntarilyUntreatedData: [],
  dpeOldVoluntarilyUntreatedData: [],
};

const dpeSlice = createSlice({
  name: 'dpe',
  initialState,
  reducers: {
    setCurrentStep(state, action: PayloadAction<number>) {
      state.currentStep = action.payload;
    },
    resetCurrentStep(state) {
      state.currentStep = null;
    },
    setSelectedDpe(state, action: PayloadAction<DpeTableData>) {
      state.selectedDpe = action.payload;
    },
    resetSelectedDpe(state) {
      state.selectedDpe = null;
    },
    setNbOwner(state, action: PayloadAction<number>) {
      state.nbOwner = action.payload;
    },
    resetNbOwner(state) {
      state.nbOwner = null;
    },
    initOwners(state) {
      const initialOwner = {
        firstname: '',
        lastname: '',
        postalCode: '',
        city: '',
        address: '',
        livingIn: true,
        email: '',
        phone: '',
        civility: 1,
        floorNumber: '',
        doorNumber: '',
        surface: 0,
        contacts: [],
        interaction: null,
      } as Owner;
      const owners = [];
      for (let i = 0; i < state.nbOwner; i++) {
        owners.push(initialOwner);
      }
      state.owners = owners;
    },
    setOwner(state, action: PayloadAction<[Owner, number]>) {
      state.owners[action.payload[1]] = action.payload[0];
    },
    addOwner(state, action: PayloadAction<Owner>) {
      state.owners.push(action.payload[0]);
      state.nbOwner += 1;
    },
    addOwnerToDpe(state, action: PayloadAction<[dpeId: number, newOwner: Owner]>) {
      const [dpeId, newOwner] = action.payload;
      const dpeToUpdate = state.dpeData.find(dpe => dpe.id === dpeId);
      if (dpeToUpdate) {
        dpeToUpdate.owners.push(newOwner);
      }
    },
    updateOwnerOfDpe(state, action: PayloadAction<[dpeId: number, updatedOwner: Owner]>) {
      const [dpeId, updatedOwner] = action.payload;
      const dpeToUpdate = state.dpeData.find(dpe => dpe.id === dpeId);
      if (dpeToUpdate) {
        const ownerIndex = dpeToUpdate.owners.findIndex(owner => owner.id === updatedOwner.id);
        if (ownerIndex >= 0) {
          dpeToUpdate.owners[ownerIndex] = { ...dpeToUpdate.owners[ownerIndex], ...updatedOwner };
        }
      }
    },
    removeOwner(state, action: PayloadAction<number>) {
      state.owners = state.owners.filter((_, index) => index !== action.payload);
      state.nbOwner -= 1;
    },
    setOwners(state, action: PayloadAction<Owner[]>) {
      state.owners = action.payload;
    },
    resetOwners(state) {
      state.owners = [];
    },
    setIsDpeIdentified(state, action: PayloadAction<boolean>) {
      state.isDpeIdentified = action.payload;
    },
    setLastCompletionType(state, action: PayloadAction<CompletionType>) {
      state.lastCompletionType = action.payload;
    },
    resetAll(state) {
      state.nbOwner = null;
      state.selectedDpe = null;
      state.currentStep = null;
      state.owners = [];
      state.isDpeIdentified = false;
      state.lastCompletionType = null;
    },
    setDpeData(state, action: PayloadAction<DpeTableData[]>) {
      state.dpeData = action.payload;
    },
    setDpeClosedData(state, action: PayloadAction<DpeTableData[]>) {
      state.dpeClosedData = action.payload;
    },
    setDpeOldData(state, action: PayloadAction<DpeTableData[]>) {
      state.dpeOldData = action.payload;
    },
    setDpeOldClosedData(state, action: PayloadAction<DpeTableData[]>) {
      state.dpeOldClosedData = action.payload;
    },
    setDpeVoluntarilyUntreatedData(state, action: PayloadAction<DpeTableData[]>) {
      state.dpeVoluntarilyUntreatedData = action.payload;
    },
    setDpeOldVoluntarilyUntreatedData(state, action: PayloadAction<DpeTableData[]>) {
      state.dpeOldVoluntarilyUntreatedData = action.payload;
    },
    setMonthFiltered(state, action: PayloadAction<string>) {
      state.monthFiltered = { date: action.payload, type: state.monthFilteredIdentifiedDpe?.type || 'reception' };
    },
    setTypeFiltered(state, action: PayloadAction<string>) {
      if (action.payload == 'reception' || action.payload == 'etablissement') {
        state.monthFilteredNewDpe = { date: state.monthFilteredNewDpe?.date, type: action.payload };
      } else {
        throw new Error('Invalid type');
      }
    },
    setMonthFilteredNewDpe(state, action: PayloadAction<string>) {
      state.monthFilteredNewDpe = { date: action.payload, type: state.monthFilteredIdentifiedDpe?.type || 'reception' };
    },
    setTypeFilteredNewDpe(state, action: PayloadAction<string>) {
      if (action.payload == 'reception' || action.payload == 'etablissement') {
        state.monthFilteredNewDpe = { date: state.monthFilteredNewDpe?.date, type: action.payload };
      } else {
        throw new Error('Invalid type');
      }
    },
    setMonthFilteredIdentifiedDpe(state, action: PayloadAction<string>) {
      state.monthFilteredIdentifiedDpe = { date: action.payload, type: state.monthFilteredIdentifiedDpe?.type || 'reception' };
    },
    setTypeFilteredIdentifiedDpe(state, action: PayloadAction<string>) {
      if (action.payload == 'reception' || action.payload == 'etablissement') {
        state.monthFilteredNewDpe = { date: state.monthFilteredNewDpe?.date, type: action.payload };
      } else {
        throw new Error('Invalid type');
      }
    },
  },
});

export const {
  setSelectedDpe,
  resetSelectedDpe,
  setCurrentStep,
  resetCurrentStep,
  setNbOwner,
  resetNbOwner,
  initOwners,
  addOwner,
  addOwnerToDpe,
  updateOwnerOfDpe,
  removeOwner,
  setOwner,
  setOwners,
  resetOwners,
  setIsDpeIdentified,
  setLastCompletionType,
  resetAll,
  setDpeData,
  setDpeClosedData,
  setDpeOldData,
  setDpeOldClosedData,
  setDpeVoluntarilyUntreatedData,
  setDpeOldVoluntarilyUntreatedData,
  setMonthFiltered,
  setMonthFilteredNewDpe,
  setMonthFilteredIdentifiedDpe,
  setTypeFiltered,
  setTypeFilteredNewDpe,
  setTypeFilteredIdentifiedDpe,
} = dpeSlice.actions;
export default dpeSlice.reducer;
